import {
    get as getUtils,
    post as postUtils,
    put as putUtils,
    patch as patchUtils,
    deleteRequest as deleteUtils,
} from '@Green-Dot-Corporation/eureka-lib-fetch-utils';
import {
    augmentHeaders,
    AUTHORIZATION_TOKEN_HEADER,
    parameterizeUrl,
} from './apiUtils';
import { appConfig } from './appConfig';
import {
    ACCESS_TOKEN,
    augment,
    authenticatedFetch,
    REFRESH_TOKEN,
} from './postFetch';
import {
    authorizedFetch,
    fetchRequestToken,
    resetAuthorizationToken,
} from './preFetch';

/**
 * PRE-LOGIN PRE-REQUISITES
 * to make a call we need authorizationToken in the header
 * to get authorizationToken, we need requestToken in the header
 * to get requestToken, we need recaptchaToken in the body or deviceToken in the header
 *
 * only startCampaignTracker API needs the requestToken - others need authorizationToken
 * when authorizationToken expires, we need to start all over again
 * The sequence to use APIs in pre-login flow is:
 * 1. call initializeAuthorization(recaptcha, iovation) once
 * 2. setTimer to clear the authorizationToken
 * 3. use post/get/put/patch methods
 */

/**
 * POST-LOGIN PRE-REQUISITES
 * upon login in, server returns accessToken and refreshToken
 * To make a post api call, we need accessToken in the header
 * when accessToken expired, we use refreshToken in the header to get new accessToken
 * when refreshToken expired, we log user out, clear cache and start over
 * The sequence to use APIs in post-login flow is:
 * 1. call initializeAuthentication(refreshToken, accessToken) upon login in
 * 2. use post/get/put/patch methods
 */

export default {};

let authorizationToken;
/**
 * Pre-login needs to make this call to initialize the session (except for startCampaignTracker)
 * @param {String} recaptchaToken - optional
 * @param {String} iovationToken - optional
 * @returns authorizationToken if successful or undefined when failed
 */
export const initializeAuthorization = async (
    recaptchaToken,
    iovationToken,
) => {
    if (!authorizationToken) {
        try {
            const requestToken = await fetchRequestToken(
                'token/os',
                recaptchaToken,
                iovationToken,
            );
            const headers = { [AUTHORIZATION_TOKEN_HEADER]: requestToken };
            const { productCode } = appConfig();
            const body = JSON.stringify({
                productKey: productCode,
            });

            const { onlinesalesaccesstoken: token } = await postUtils(
                '/api/v1/auth/token/os',
                augmentHeaders({ headers, body }),
            );
            authorizationToken = token;

            // TODO: remove this when resetAuthorizationToken is retired
            resetAuthorizationToken(token);
        } catch (_) {
            return undefined;
        }
    }

    return authorizationToken;
};

/**
 * This method is to either set or clear the authorization. Once verify-code is performed,
 * a new elevated authorized token is returned, which will need to be set here. After user is created or logged in,
 * we need to clear this authorization token so that post-login APIs will use authentication token.
 * @param {} token
 */
export const resetAuthorization = (token) => {
    authorizationToken = token;
    resetAuthorizationToken(token);
};

/**
 * Boolean indicating if the pre-login session has been authorized
 * @returns boolean
 */
export const isAuthorized = () => !!authorizationToken;

/**
 * This method stores the refreshToken and accessToken returned from a successful user logging in.
 * @param {String} refreshToken
 * @param {String} accessToken
 */
export const initializeAuthentication = (refreshToken, accessToken) => {
    resetAuthorization(); // make sure auth is cleared
    sessionStorage.setItem(REFRESH_TOKEN, refreshToken);
    sessionStorage.setItem(ACCESS_TOKEN, accessToken);
};

/**
 * This method clears both refreshToken and accessToken from session. This method is called when
 * user is logging out or when refreshToken expires.
 */
export const clearAuthentication = () => {
    sessionStorage.clear();
};

/**
 * Boolean indicating both refreshToken and accessToken are valid
 * @returns boolean
 */
export const isAuthenticated = () => {
    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN);
    const accessToken = sessionStorage.getItem(ACCESS_TOKEN);

    return !!refreshToken && !!accessToken;
};

const fetch = async (method, url, options, isLegacy = false) => {
    if (!isAuthorized() && !isAuthenticated()) {
        return {
            errorCode: 0,
            statusCode: 0,
            message: 'INVALID_SESSION_TOKEN',
        };
    }

    const invoker = isAuthorized() ? authorizedFetch : authenticatedFetch;
    const headerAugmenter = isAuthorized() ? augmentHeaders : augment;

    return invoker(
        method,
        parameterizeUrl(url),
        headerAugmenter(options, isLegacy),
    );
};

export const get = async (url, options, isLegacy = false) => {
    return fetch(getUtils, url, options, isLegacy);
};

export const post = async (url, options, isLegacy = false) => {
    return fetch(postUtils, url, options, isLegacy);
};

export const put = async (url, options, isLegacy = false) => {
    return fetch(putUtils, url, options, isLegacy);
};

export const patch = async (url, options, isLegacy = false) => {
    return fetch(patchUtils, url, options, isLegacy);
};

export const deleteRequest = async (url, options, isLegacy = false) => {
    return fetch(deleteUtils, url, options, isLegacy);
};
